import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { getPlatform } from "@/hooks/usePlatform";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ConnectivityProvider } from "@/contexts/ConnectivityContext";
import { AppLayout } from "@/components/AppLayout";
import { PageTransition } from "@/components/PageTransition";
import { ActiveStyleProvider } from "@/contexts/ActiveStyleContext";
import { useRoles } from "@/hooks/useRoles";
import { lazy, Suspense, useEffect } from "react";
import { PageLoader } from "@/components/PageLoader";

// Eagerly loaded (small, critical path)
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";

// Lazy-loaded pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Tracking = lazy(() => import("./pages/Tracking"));
const Workouts = lazy(() => import("./pages/Workouts"));
const Profile = lazy(() => import("./pages/Profile"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const UsagePolicy = lazy(() => import("./pages/UsagePolicy"));
const Admin = lazy(() => import("./pages/Admin"));
const BodyMetrics = lazy(() => import("./pages/BodyMetrics"));
const AiCoach = lazy(() => import("./pages/AiCoach"));
const AiCoachActivity = lazy(() => import("./pages/AiCoachActivity"));
const OfflineSettings = lazy(() => import("./pages/OfflineSettings"));
const CoachDashboard = lazy(() => import("./pages/CoachDashboard"));
const MyCoach = lazy(() => import("./pages/MyCoach"));
const FindCoach = lazy(() => import("./pages/FindCoach"));
const Landing = lazy(() => import("./pages/Landing"));
const Install = lazy(() => import("./pages/Install"));
const ReferralRedirect = lazy(() => import("./pages/ReferralRedirect"));
const GymWallet = lazy(() => import("./pages/GymWallet"));
const Friends = lazy(() => import("./pages/Friends"));
const ActivityFeed = lazy(() => import("./pages/ActivityFeed"));
const Challenges = lazy(() => import("./pages/Challenges"));
const SocialEnable = lazy(() => import("./pages/SocialEnable"));
const SocialSettings = lazy(() => import("./pages/SocialSettings"));

const queryClient = new QueryClient();

const isNativeApp = getPlatform() !== "web";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader fullScreen />;
  if (!user) return <Navigate to={isNativeApp ? "/auth" : "/landing"} replace />;
  return <AppLayout><PageTransition>{children}</PageTransition></AppLayout>;
}

function LandingRoute({ children }: { children: React.ReactNode }) {
  if (isNativeApp) return <Navigate to="/auth" replace />;
  return <PageTransition>{children}</PageTransition>;
}

function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return <PageTransition>{children}</PageTransition>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: rolesLoading } = useRoles();
  if (authLoading || rolesLoading) return <PageLoader fullScreen />;
  if (!user) return <Navigate to="/auth" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return <AppLayout><PageTransition>{children}</PageTransition></AppLayout>;
}

function CoachRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { isCoach, loading: rolesLoading } = useRoles();
  if (authLoading || rolesLoading) return <PageLoader fullScreen />;
  if (!user) return <Navigate to="/auth" replace />;
  if (!isCoach) return <Navigate to="/" replace />;
  return <AppLayout><PageTransition>{children}</PageTransition></AppLayout>;
}

function GlobalErrorBoundary({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      console.error("Unhandled rejection caught globally:", event.reason);
      event.preventDefault(); // Prevent browser from crashing / reloading the page
    };
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);
  return <>{children}</>;
}

const App = () => (
  <GlobalErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <ConnectivityProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <ActiveStyleProvider>
              <Suspense fallback={<PageLoader fullScreen />}>
                <Routes>
                  <Route path="/landing" element={<LandingRoute><Suspense fallback={<PageLoader fullScreen />}><Landing /></Suspense></LandingRoute>} />
                  <Route path="/install" element={<LandingRoute><Suspense fallback={<PageLoader fullScreen />}><Install /></Suspense></LandingRoute>} />
                  <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/macro-calculator" element={<Navigate to="/tracking" replace />} />
                  <Route path="/tracking" element={<ProtectedRoute><Tracking /></ProtectedRoute>} />
                  <Route path="/orm-calculator" element={<Navigate to="/workouts" replace />} />
                  <Route path="/workouts" element={<ProtectedRoute><Workouts /></ProtectedRoute>} />
                  <Route path="/gym-wallet" element={<ProtectedRoute><GymWallet /></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                  <Route path="/glossary" element={<Navigate to="/profile" replace />} />
                  <Route path="/privacy" element={<Suspense fallback={<PageLoader fullScreen />}><PrivacyPolicy /></Suspense>} />
                  <Route path="/usage-policy" element={<Suspense fallback={<PageLoader fullScreen />}><UsagePolicy /></Suspense>} />
                  <Route path="/body-metrics" element={<ProtectedRoute><BodyMetrics /></ProtectedRoute>} />
                  <Route path="/ai-coach" element={<ProtectedRoute><AiCoach /></ProtectedRoute>} />
                  <Route path="/my-coach" element={<ProtectedRoute><MyCoach /></ProtectedRoute>} />
                  <Route path="/find-coach" element={<ProtectedRoute><FindCoach /></ProtectedRoute>} />
                  <Route path="/ai-coach/activity" element={<AdminRoute><AiCoachActivity /></AdminRoute>} />
                  <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
                  <Route path="/coach" element={<CoachRoute><CoachDashboard /></CoachRoute>} />
                  <Route path="/offline-settings" element={<ProtectedRoute><OfflineSettings /></ProtectedRoute>} />
                  <Route path="/friends" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
                  <Route path="/activity" element={<ProtectedRoute><ActivityFeed /></ProtectedRoute>} />
                  <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
                  <Route path="/social/enable" element={<ProtectedRoute><SocialEnable /></ProtectedRoute>} />
                  <Route path="/social/settings" element={<ProtectedRoute><SocialSettings /></ProtectedRoute>} />
                  <Route path="/auth" element={<AuthRoute><Auth /></AuthRoute>} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/r/:code" element={<Suspense fallback={<PageLoader fullScreen />}><ReferralRedirect /></Suspense>} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              </ActiveStyleProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ConnectivityProvider>
    </ThemeProvider>
  </QueryClientProvider>
  </GlobalErrorBoundary>
);

export default App;
